import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/hooks/useAuth";
import {
  PaginatedArticles,
  Article,
  UserInfo,
  UserStamp,
} from "@/types/canonical";
import { SimpleTable } from "@/components/SimpleTable";
import { ColumnDef, PaginationState, Updater } from "@tanstack/react-table";
import { useDebounce } from "@/hooks/useDebounce";
import { ListFilter } from "lucide-react";

// --- localStorage Keys ---
const LS_PERSISTENT_STATUS_KEY = "articleListPersistentStatus";
const LS_SORT_BY_KEY = "articleListSortBy";
const LS_SORT_DESC_KEY = "articleListSortDesc";
const LS_ASSIGNED_TO_KEY = "articleListAssignedTo";
const LS_RELEASE_VERSION_KEY = "articleListReleaseVersion";
const LS_USER_STATUS_FILTERS_KEY = "articleListUserStatusFilters";
// --- End localStorage Keys ---

// Define valid statuses near the top of the file
const VALID_AGG_STATUSES = [
  "update_needed",
  "no_update_needed",
  "not_analyzed",
] as const;
type AggregatedStatus = (typeof VALID_AGG_STATUSES)[number]; // Type for one status

// Mapping for display text (can be placed before the component)
const AGG_STATUS_DISPLAY_TEXT: Record<AggregatedStatus | string, string> = {
  update_needed: "Update Needed",
  no_update_needed: "No Update Needed",
  not_analyzed: "Not Analyzed",
  // Keep these for backward compatibility with localStorage if needed, but prefer the above keys
  true: "Update Needed", // Map old value
  false: "No Update Needed", // Map old value
  null: "Not Analyzed", // Map old value
};

// Helper function replacement
const getAggregatedStatusDisplayText = (
  value: AggregatedStatus | string,
): string => {
  return AGG_STATUS_DISPLAY_TEXT[value] || value; // Fallback to value itself if not found
};

const VALID_USER_STATUSES = [
  "Open",
  "Needs Review",
  "In Progress",
  "Completed",
  "N/A",
];

type UserStatus = (typeof VALID_USER_STATUSES)[number];

const USER_STATUS_DISPLAY_TEXT: Record<UserStatus | string, string> = {
  Open: "Open",
  "Needs Review": "Needs Review",
  "In Progress": "In Progress",
  Completed: "Completed",
  "N/A": "N/A",
};

const getUserStatusDisplayText = (value: UserStatus | string): string => {
  return USER_STATUS_DISPLAY_TEXT[value] || value; // Fallback to value itself if not found
};

interface ArticlesListPageProps {
  apiBaseUrl: string;
}

interface GetColumnsProps {
  apiBaseUrl: string;
  sortBy: string;
  sortDesc: boolean;
  updateNeededFilters: string[];
  userStatusFilters: string[];
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  dropdownContentRef: React.RefObject<HTMLDivElement | null>;
  isDropdownOpen: boolean;
  setIsDropdownOpen: (isOpen: boolean) => void;
  setSortBy: (sortBy: string) => void;
  setSortDesc: (sortDesc: boolean) => void;
  setPage: (page: number) => void;
  handleTempFilterChange: (value: string, checked: boolean) => void;
  resetUpdateNeededFilters: () => void;
  getUserDisplayName: (userInfo?: UserInfo | null) => string;
  getAvatarFallback: (userInfo?: UserInfo | null) => string;
  handleUserStatusFilterChange: (value: string, checked: boolean) => void;
  resetUserStatusFilters: () => void;
}

// Helper function to construct the full avatar URL
const constructAvatarUrl = (
  apiBaseUrl: string,
  relativePath?: string | null,
): string | undefined => {
  if (!relativePath) return undefined;

  // If it's already an absolute URL (though backend should provide relative)
  if (relativePath.match(/^https?:\/\//)) {
    return relativePath;
  }

  // Derive the base URL for static assets by removing /api suffix and trailing slash
  const staticBaseUrl = apiBaseUrl.replace(/(\/api)?\/?$/, "");

  // Ensure no double slashes if relativePath starts with /
  const cleanRelativePath = relativePath.startsWith("/")
    ? relativePath.substring(1)
    : relativePath;
  return `${staticBaseUrl}/${cleanRelativePath}`;
};

const getStatusColor = (status: string | null) => {
  switch (status?.toLowerCase()) {
    case "open":
      return "bg-primary text-primary-content";
    case "needs review":
      return "bg-secondary text-secondary-content";
    case "in progress":
      return "bg-secondary text-secondary-content";
    case "completed":
      return "bg-success text-success-content";
    case "n/a":
      return "bg-success text-success-content";
    default:
      return "bg-base-content/5 text-base-content/70";
  }
};

// Helper to extract ALL release note versions with their update_needed status
const getReleaseVersionsWithStatus = (
  article: Article,
): { version: string; status: "true" | "false" | "mixed" }[] => {
  const versionStatusMap = new Map<string, Set<boolean>>();

  // Collect all versions and track all their update_needed statuses
  article.analysis_results.forEach((result) => {
    const version = result.release_note_version;
    const updateNeeded = result.update_needed === true;

    if (!versionStatusMap.has(version)) {
      versionStatusMap.set(version, new Set());
    }
    versionStatusMap.get(version)!.add(updateNeeded);
  });

  // Convert to array with status determination and sort
  return Array.from(versionStatusMap.entries())
    .map(([version, statusSet]) => {
      let status: "true" | "false" | "mixed";

      if (statusSet.size > 1) {
        // Both true and false exist for this version
        status = "mixed";
      } else {
        // Only one status exists
        status = statusSet.has(true) ? "true" : "false";
      }

      return { version, status };
    })
    .sort((a, b) => {
      // Parse versions: 25.11, 24.4, 23.12 -> sort by major.minor (descending)
      const [aMajor, aMinor] = a.version.split(".").map(Number);
      const [bMajor, bMinor] = b.version.split(".").map(Number);
      // Sort major version descending (25 > 24 > 23)
      // Then minor version descending (12 > 11 > 4)
      return bMajor - aMajor || bMinor - aMinor;
    });
};

// Helper to get badge styling based on status
const getBadgeStyles = (status: "true" | "false" | "mixed"): string => {
  switch (status) {
    case "true":
      // Red for update needed (matches DaisyUI error colors)
      return "bg-red-100 text-red-800 border-red-200 hover:bg-red-200";
    case "false":
      // Green for no update needed (matches DaisyUI success colors)
      return "bg-green-100 text-green-800 border-green-200 hover:bg-green-200";
    case "mixed":
      // Orange for mixed statuses
      return "bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

// Helper to get display text for update needed filter values
// DEPRECATED - Use getAggregatedStatusDisplayText
/*
const getUpdateNeededDisplayText = (value: string): string => {
  switch (value) {
    case 'true': return 'Yes';
    case 'false': return 'No';
    case 'null': return 'N/A';
    default: return '';
  }
};
*/

// --- Column Definition Factory ---
const getColumns = ({
  apiBaseUrl,
  sortBy,
  sortDesc,
  updateNeededFilters,
  userStatusFilters,
  dropdownRef,
  dropdownContentRef,
  isDropdownOpen,
  setIsDropdownOpen,
  setSortBy,
  setSortDesc,
  setPage,
  handleTempFilterChange,
  resetUpdateNeededFilters,
  getUserDisplayName,
  getAvatarFallback,
  handleUserStatusFilterChange,
  resetUserStatusFilters,
}: GetColumnsProps): ColumnDef<Article>[] => [
  {
    accessorKey: "title",
    header: () => (
      <button
        className="btn btn-ghost btn-sm"
        onClick={() => {
          const newSortDesc = sortBy === "title" ? !sortDesc : true;
          setSortBy("title");
          setSortDesc(newSortDesc);
          setPage(1);
        }}
      >
        Title
        <span className="ml-2 text-xs">
          {sortBy === "title" ? (sortDesc ? "▼" : "▲") : ""}
        </span>
      </button>
    ),
    cell: ({ row }) => {
      return (
        <Link
          to={`/articles/${row.original._id}`}
          className="link-primary-content link font-medium link-hover"
        >
          {row.getValue("title")}
        </Link>
      );
    },
    enableSorting: false,
    meta: { style: { minWidth: "300px", width: "35%" } },
  },
  {
    accessorKey: "release_versions",
    header: () => <div className="text-left">Release Versions</div>,
    cell: ({ row }) => {
      const versions = getReleaseVersionsWithStatus(row.original);

      if (versions.length === 0) {
        return <span className="pointer-events-none opacity-50">-</span>;
      }

      // Show first 3 versions, then "+X more" if there are more
      const displayVersions = versions.slice(0, 3);
      const remainingCount = versions.length - 3;

      return (
        <div className="dropdown-hover dropdown">
          <div
            tabIndex={0}
            className="flex cursor-pointer flex-wrap items-center gap-1"
          >
            {displayVersions.map(({ version, status }) => (
              <div
                key={version}
                className={`inline-flex items-center rounded-full border px-2 py-1 font-mono text-xs ${getBadgeStyles(status)}`}
              >
                {version}
              </div>
            ))}
            {remainingCount > 0 && (
              <div className="inline-flex items-center rounded-full border border-gray-300 bg-gray-100 px-2 py-1 font-mono text-xs text-gray-600">
                +{remainingCount}
              </div>
            )}
          </div>
          {versions.length > 3 && (
            <div className="dropdown-content menu z-50 max-w-xs rounded-box border bg-base-100 p-2 shadow-lg">
              <div className="mb-2 text-sm font-semibold">
                All Release Versions:
              </div>
              <div className="flex flex-wrap gap-1">
                {versions.map(({ version, status }) => (
                  <div
                    key={version}
                    className={`inline-flex items-center rounded-full border px-2 py-1 font-mono text-xs ${getBadgeStyles(status)}`}
                    title={
                      status === "mixed"
                        ? "Mixed Status (Some need updates, some don't)"
                        : status === "true"
                          ? "Update Needed"
                          : "No Update Needed"
                    }
                  >
                    {version}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    },
    enableSorting: false,
    meta: { style: { minWidth: "140px", width: "15%" } },
  },
  {
    accessorKey: "persistent_status",
    header: () => (
      <>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => {
            const newSortDesc =
              sortBy === "persistent_status" ? !sortDesc : true;
            setSortBy("persistent_status");
            setSortDesc(newSortDesc);
            setPage(1);
          }}
        >
          User Status
          <span className="ml-2 text-xs">
            {sortBy === "persistent_status" ? (sortDesc ? "▼" : "▲") : ""}
          </span>
        </button>
        {/* DaisyUI Dropdown - Controlled using div structure */}
        <div
          ref={dropdownRef}
          className={`dropdown dropdown-center ${isDropdownOpen ? "dropdown-open" : ""}`}
        >
          <div
            tabIndex={0}
            role="button"
            className="btn ml-1 btn-ghost btn-xs"
            onClick={() => {
              setIsDropdownOpen(!isDropdownOpen);
            }}
          >
            <ListFilter className="h-4 w-4" />
          </div>
          <div
            ref={dropdownContentRef}
            className="dropdown-content menu z-[1] flex min-w-max flex-col justify-between gap-2 rounded-box bg-base-100 p-4 text-base-content shadow backdrop-blur-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            {VALID_USER_STATUSES.map((value) => (
              <label
                key={value}
                className="label flex cursor-pointer items-center justify-between rounded px-2 py-1 hover:bg-base-content/10"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const input = e.currentTarget.querySelector<HTMLInputElement>(
                    'input[type="checkbox"]',
                  );
                  if (input) {
                    handleUserStatusFilterChange(value, !input.checked);
                  }
                }}
              >
                <span className="label-text text-sm text-base-content/70">
                  {getUserStatusDisplayText(value)}
                </span>
                <input
                  type="checkbox"
                  className="checkbox ml-2 checkbox-sm checkbox-primary"
                  checked={userStatusFilters.includes(value)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleUserStatusFilterChange(value, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </label>
            ))}
            <div className="divider my-1"></div>
            <div className="flex justify-end px-1">
              <button
                className="btn text-error btn-ghost btn-xs"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  resetUserStatusFilters();
                }}
                title="Reset filters to show all"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </>
    ),
    cell: ({ row }) => {
      const status = row.getValue("persistent_status") as string | undefined;
      const displayStatus = status;

      if (!displayStatus) {
        return <span className="pointer-events-none opacity-50">-</span>;
      }

      const badgeClass = getStatusColor(displayStatus);

      return (
        <div
          className={`badge ${badgeClass} pointer-events-none cursor-default border-none font-mono text-xs shadow-md`}
        >
          {displayStatus}
        </div>
      );
    },
    enableSorting: false,
    meta: { style: { minWidth: "140px", width: "15%" } },
  },
  {
    accessorKey: "assigned_to",
    header: () => (
      <>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => {
            const sortKey = "assigned_to.username";
            const newSortDesc = sortBy === sortKey ? !sortDesc : true;
            setSortBy(sortKey);
            setSortDesc(newSortDesc);
            setPage(1);
          }}
        >
          Assigned To
          <span className="ml-2 text-xs">
            {sortBy === "assigned_to.username" ? (sortDesc ? "▼" : "▲") : ""}
          </span>
        </button>
        {/* Filter button removed for simplicity, add back if needed */}
        {/* <button className="btn btn-ghost btn-xs">
              <ListFilter className="w-4 h-4" />
            </button> */}
      </>
    ),
    cell: ({ row }) => {
      const assignedUser = row.getValue("assigned_to") as UserStamp | undefined;
      if (!assignedUser) {
        return <span className="pointer-events-none opacity-50">-</span>;
      }
      const fullAvatarUrl = constructAvatarUrl(
        apiBaseUrl,
        assignedUser.avatar_url,
      );
      return (
        <div className="pointer-events-none flex items-center gap-2">
          <div
            className={`avatar-xs avatar ${!fullAvatarUrl ? "placeholder" : ""}`}
          >
            <div className="h-6 w-6 rounded-full bg-neutral text-neutral-content">
              {fullAvatarUrl ? (
                <img
                  src={fullAvatarUrl}
                  alt={getUserDisplayName(assignedUser)}
                />
              ) : (
                <span className="text-xs">
                  {getAvatarFallback(assignedUser)}
                </span>
              )}
            </div>
          </div>
          <span className="text-xs">{getUserDisplayName(assignedUser)}</span>
        </div>
      );
    },
    enableSorting: false,
    meta: { style: { minWidth: "160px", width: "15%" } },
  },
  {
    accessorKey: "aggregated_analysis_status",
    header: () => (
      <div className="flex items-center">
        {" "}
        {/* Wrapper for alignment */}
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => {
            const sortKey = "aggregated_analysis_status";
            const newSortDesc = sortBy === sortKey ? !sortDesc : true;
            setSortBy(sortKey);
            setSortDesc(newSortDesc);
            setPage(1);
          }}
        >
          AI Status
          <span className="ml-2 text-xs">
            {sortBy === "aggregated_analysis_status"
              ? sortDesc
                ? "▼"
                : "▲"
              : ""}
          </span>
        </button>
        {/* DaisyUI Dropdown - Controlled using div structure */}
        <div
          ref={dropdownRef}
          className={`dropdown dropdown-end ${isDropdownOpen ? "dropdown-open" : ""}`}
        >
          <div
            tabIndex={0}
            role="button"
            className="btn ml-1 btn-ghost btn-xs"
            onClick={() => {
              setIsDropdownOpen(!isDropdownOpen);
            }}
          >
            <ListFilter className="h-4 w-4" />
          </div>
          <div
            ref={dropdownContentRef}
            className="dropdown-content menu z-[1] flex min-w-max flex-col justify-between gap-2 rounded-box bg-base-100 p-4 text-base-content shadow backdrop-blur-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            {VALID_AGG_STATUSES.map((value) => (
              <label
                key={value}
                className="label flex cursor-pointer items-center justify-between rounded px-2 py-1 hover:bg-base-content/10"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const input = e.currentTarget.querySelector<HTMLInputElement>(
                    'input[type="checkbox"]',
                  );
                  if (input) {
                    handleTempFilterChange(value, !input.checked);
                  }
                }}
              >
                <span className="label-text text-sm text-base-content/70">
                  {getAggregatedStatusDisplayText(value)}
                </span>
                <input
                  type="checkbox"
                  className="checkbox ml-2 checkbox-sm checkbox-primary"
                  checked={updateNeededFilters.includes(value)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleTempFilterChange(value, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </label>
            ))}
            <div className="divider my-1"></div>
            <div className="flex justify-end px-1">
              <button
                className="btn text-error btn-ghost btn-xs"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  resetUpdateNeededFilters();
                }}
                title="Reset filters to show all"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
    ),
    cell: ({ row }) => {
      const status = row.getValue("aggregated_analysis_status") as
        | AggregatedStatus
        | undefined
        | null;
      switch (status) {
        case "update_needed":
          return (
            <div className="pointer-events-none badge font-mono text-xs shadow-md badge-error select-none">
              <span className="text-error-content">Update Needed</span>
            </div>
          );
        case "no_update_needed":
          return (
            <div className="pointer-events-none badge font-mono text-xs shadow-md badge-success select-none">
              <span className="text-success-content">No Update Needed</span>
            </div>
          );
        case "not_analyzed":
          return (
            <div className="pointer-events-none badge font-mono text-xs shadow-md badge-neutral select-none">
              <span>Not Analyzed</span>
            </div>
          );
        default:
          return <span className="pointer-events-none opacity-50">-</span>;
      }
    },
    enableSorting: false,
    meta: { style: { minWidth: "130px", width: "10%" } },
  },
];

const ArticlesListPage: React.FC<ArticlesListPageProps> = ({ apiBaseUrl }) => {
  const { token } = useAuth();
  const [data, setData] = useState<PaginatedArticles | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allReleaseVersions, setAllReleaseVersions] = useState<string[]>([]);

  // Cache for client-side filtering
  const [cachedFilterData, setCachedFilterData] = useState<{
    data: PaginatedArticles | null;
    filters: string;
  }>({ data: null, filters: "" });

  // State for filters and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [persistentStatusFilter, setPersistentStatusFilter] = useState<string>(
    () => {
      return localStorage.getItem(LS_PERSISTENT_STATUS_KEY) || "all";
    },
  );
  const [aggregatedStatusFilters, setAggregatedStatusFilters] = useState<
    AggregatedStatus[]
  >(() => {
    const saved = localStorage.getItem("articleListAggregatedStatusFilters");
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as string[];
        const mapped = parsed
          .map((val) => {
            if (val === "true") return "update_needed";
            if (val === "false") return "no_update_needed";
            if (val === "null") return "not_analyzed";
            return val;
          })
          .filter((val) =>
            VALID_AGG_STATUSES.includes(val as AggregatedStatus),
          );
        return mapped.length > 0
          ? (mapped as AggregatedStatus[])
          : [...VALID_AGG_STATUSES];
      } catch (e) {
        console.error(
          "Failed to parse aggregatedStatusFilters from localStorage",
          e,
        );
        return [...VALID_AGG_STATUSES];
      }
    } else {
      return [...VALID_AGG_STATUSES];
    }
  });
  const [assignedToFilter, setAssignedToFilter] = useState<string>(() => {
    return localStorage.getItem(LS_ASSIGNED_TO_KEY) || "all";
  });
  const [releaseVersionFilter, setReleaseVersionFilter] = useState<string>(
    () => {
      return localStorage.getItem(LS_RELEASE_VERSION_KEY) || "all";
    },
  );
  const [userStatusFilters, setUserStatusFilters] = useState<UserStatus[]>(
    () => {
      const saved = localStorage.getItem(LS_USER_STATUS_FILTERS_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved) as string[];
          const filtered = parsed.filter((val) =>
            VALID_USER_STATUSES.includes(val as UserStatus),
          );
          return filtered.length > 0
            ? (filtered as UserStatus[])
            : [...VALID_USER_STATUSES];
        } catch (e) {
          console.error(
            "Failed to parse userStatusFilters from localStorage",
            e,
          );
          return [...VALID_USER_STATUSES];
        }
      } else {
        return [...VALID_USER_STATUSES];
      }
    },
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState<string>(() => {
    return localStorage.getItem(LS_SORT_BY_KEY) || "db_last_updated";
  });
  const [sortDesc, setSortDesc] = useState<boolean>(() => {
    const storedVal = localStorage.getItem(LS_SORT_DESC_KEY);
    return storedVal ? JSON.parse(storedVal) : true;
  });

  // Add state for dropdown control
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Refs for the dropdown
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const dropdownContentRef = useRef<HTMLDivElement | null>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (!token) {
      setError("Authentication token not found. Please login.");
      setLoading(false);
      return;
    }

    // When user status filtering is active, we need to fetch all data for reliable pagination
    const isUserStatusFiltering =
      userStatusFilters.length < VALID_USER_STATUSES.length &&
      userStatusFilters.length > 0;

    // Create cache key for filtering (excluding page since we paginate client-side)
    const filterCacheKey = isUserStatusFiltering
      ? JSON.stringify({
          search: debouncedSearchTerm,
          persistentStatus: persistentStatusFilter,
          aggregatedStatus: aggregatedStatusFilters.sort(),
          userStatus: userStatusFilters.sort(),
          assignedTo: assignedToFilter,
          releaseVersion: releaseVersionFilter,
          sortBy,
          sortDesc,
        })
      : "";

    // Check cache for user status filtering
    if (
      isUserStatusFiltering &&
      cachedFilterData.filters === filterCacheKey &&
      cachedFilterData.data
    ) {
      // Use cached data and apply pagination
      const allFilteredArticles = cachedFilterData.data.articles.filter(
        (article) => {
          const articleStatus = article.persistent_status;
          return (
            articleStatus &&
            userStatusFilters.includes(articleStatus as UserStatus)
          );
        },
      );

      // Apply pagination to cached filtered results
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedArticles = allFilteredArticles.slice(startIndex, endIndex);

      const filteredData = {
        ...cachedFilterData.data,
        articles: paginatedArticles,
        total_articles: allFilteredArticles.length,
        total_pages: Math.ceil(allFilteredArticles.length / pageSize),
      };

      setData(filteredData);
      setLoading(false);
      return;
    }

    try {
      const params = new URLSearchParams({
        page: isUserStatusFiltering ? "1" : page.toString(),
        page_size: isUserStatusFiltering ? "1000" : pageSize.toString(), // Fetch all data when filtering
        sort_by: sortBy,
        sort_desc: sortDesc.toString(),
      });
      if (debouncedSearchTerm) {
        params.append("search_title", debouncedSearchTerm);
      }
      // Only apply single persistent status filter if user status multi-filter is not active
      if (
        persistentStatusFilter &&
        persistentStatusFilter !== "all" &&
        !isUserStatusFiltering
      ) {
        params.append("persistent_status", persistentStatusFilter);
      }
      // --- Apply aggregated status filter logic ---
      const allStatusesSelected =
        aggregatedStatusFilters.length === VALID_AGG_STATUSES.length;
      if (!allStatusesSelected && aggregatedStatusFilters.length > 0) {
        params.append("aggregated_status", aggregatedStatusFilters.join(","));
      }
      // --- End aggregated status filter logic ---

      // --- Apply user status filter logic ---
      const allUserStatusesSelected =
        userStatusFilters.length === VALID_USER_STATUSES.length;
      if (!allUserStatusesSelected && userStatusFilters.length > 0) {
        // Note: Backend expects 'persistent_status' but only supports single value, not comma-separated
        // For now, we'll implement client-side filtering for multiple user status selections
        // TODO: Update backend to support comma-separated persistent_status values
      }
      // --- End user status filter logic ---

      if (assignedToFilter && assignedToFilter !== "all") {
        params.append("assigned_to_user", assignedToFilter);
      }
      if (releaseVersionFilter && releaseVersionFilter !== "all") {
        params.append("release_version", releaseVersionFilter);
      }
      const response = await axios.get<PaginatedArticles>(
        `${apiBaseUrl}/canonical/articles?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      let filteredData = response.data;

      // Apply client-side user status filtering if not all statuses are selected
      if (isUserStatusFiltering) {
        // Cache the raw data for future pagination
        setCachedFilterData({
          data: response.data,
          filters: filterCacheKey,
        });

        const allFilteredArticles = response.data.articles.filter((article) => {
          const articleStatus = article.persistent_status;
          return (
            articleStatus &&
            userStatusFilters.includes(articleStatus as UserStatus)
          );
        });

        // Apply pagination to filtered results
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedArticles = allFilteredArticles.slice(
          startIndex,
          endIndex,
        );

        filteredData = {
          ...response.data,
          articles: paginatedArticles,
          total_articles: allFilteredArticles.length,
          total_pages: Math.ceil(allFilteredArticles.length / pageSize),
        };

        console.debug(
          `Client-side user status filtering: ${response.data.articles.length} -> ${allFilteredArticles.length} articles (showing ${paginatedArticles.length} on page ${page})`,
        );
      }

      setData(filteredData);
    } catch (err: any) {
      console.error("Error fetching articles:", err);
      let errorMsg = "Failed to fetch articles";
      if (axios.isAxiosError(err) && err.response) {
        errorMsg = err.response.data?.detail || err.message;
      } else if (err instanceof Error) {
        errorMsg = err.message;
      }
      setError(errorMsg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [
    token,
    apiBaseUrl,
    page,
    pageSize,
    sortBy,
    sortDesc,
    debouncedSearchTerm,
    persistentStatusFilter,
    aggregatedStatusFilters,
    userStatusFilters,
    assignedToFilter,
    releaseVersionFilter,
    cachedFilterData.filters, // Add cache dependency
  ]);

  // Separate function to fetch all release versions (independent of pagination)
  const fetchAllReleaseVersions = useCallback(async () => {
    if (!token) return;

    try {
      const response = await axios.get<string[]>(
        `${apiBaseUrl}/canonical/articles/release-versions`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setAllReleaseVersions(response.data);
      console.log("Fetched all release versions:", response.data);
    } catch (err: any) {
      console.error("Error fetching release versions:", err);
      // Don't set error state for this, as it's not critical to the main functionality
      setAllReleaseVersions([]);
    }
  }, [token, apiBaseUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]); // Keep dependencies here

  // Fetch release versions on component mount
  useEffect(() => {
    fetchAllReleaseVersions();
  }, [fetchAllReleaseVersions]);

  // Clear cache when filters change (except page and userStatusFilters which are handled separately)
  useEffect(() => {
    setCachedFilterData({ data: null, filters: "" });
  }, [
    debouncedSearchTerm,
    persistentStatusFilter,
    aggregatedStatusFilters,
    assignedToFilter,
    releaseVersionFilter,
    sortBy,
    sortDesc,
  ]);

  // --- Save filters and sorting to localStorage ---
  useEffect(() => {
    localStorage.setItem(LS_PERSISTENT_STATUS_KEY, persistentStatusFilter);
  }, [persistentStatusFilter]);
  useEffect(() => {
    localStorage.setItem(
      "articleListAggregatedStatusFilters",
      JSON.stringify(aggregatedStatusFilters),
    );
  }, [aggregatedStatusFilters]);
  useEffect(() => {
    localStorage.setItem(LS_SORT_BY_KEY, sortBy);
  }, [sortBy]);
  useEffect(() => {
    localStorage.setItem(LS_SORT_DESC_KEY, JSON.stringify(sortDesc));
  }, [sortDesc]);
  useEffect(() => {
    localStorage.setItem(LS_ASSIGNED_TO_KEY, assignedToFilter);
  }, [assignedToFilter]);
  useEffect(() => {
    localStorage.setItem(LS_RELEASE_VERSION_KEY, releaseVersionFilter);
  }, [releaseVersionFilter]);
  useEffect(() => {
    localStorage.setItem(
      LS_USER_STATUS_FILTERS_KEY,
      JSON.stringify(userStatusFilters),
    );
  }, [userStatusFilters]);
  // --- END Save ---

  // Update click outside handler to use state setter
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]); // Re-run when isDropdownOpen changes

  // --- Callbacks passed to columns ---
  const handleTempFilterChange = useCallback(
    (value: AggregatedStatus, checked: boolean) => {
      // Clear cache when other filters change
      setCachedFilterData({ data: null, filters: "" });

      setAggregatedStatusFilters((prev) => {
        const newFilters = checked
          ? [...prev, value]
          : prev.filter((v) => v !== value);
        // Reset to page 1 when filter changes
        setPage(1);
        return newFilters;
      });
    },
    [setAggregatedStatusFilters, setPage],
  );

  const resetUpdateNeededFilters = useCallback(() => {
    // Clear cache when filter resets
    setCachedFilterData({ data: null, filters: "" });
    setAggregatedStatusFilters([...VALID_AGG_STATUSES]);
    setPage(1);
  }, [setAggregatedStatusFilters, setPage]);

  const handleUserStatusFilterChange = useCallback(
    (value: UserStatus, checked: boolean) => {
      // Clear cache when filter changes
      setCachedFilterData({ data: null, filters: "" });

      setUserStatusFilters((prev) => {
        const newFilters = checked
          ? [...prev, value]
          : prev.filter((v) => v !== value);
        // Reset to page 1 when filter changes
        setPage(1);

        // If we're starting to use multi-filter, reset single filter
        if (
          newFilters.length < VALID_USER_STATUSES.length &&
          persistentStatusFilter !== "all"
        ) {
          setPersistentStatusFilter("all");
        }

        return newFilters;
      });
    },
    [setUserStatusFilters, setPage, persistentStatusFilter],
  );

  const resetUserStatusFilters = useCallback(() => {
    // Clear cache when filter resets
    setCachedFilterData({ data: null, filters: "" });
    setUserStatusFilters([...VALID_USER_STATUSES]);
    setPage(1);
  }, [setUserStatusFilters, setPage]);

  // Helper function to get user display name (can stay inside or move out)
  const getUserDisplayName = useCallback(
    (userInfo?: UserInfo | null): string => {
      if (!userInfo) return "-";
      return userInfo.name || userInfo.username; // Prefer name, fallback to username
    },
    [],
  );

  // Helper function to get avatar fallback (can stay inside or move out)
  const getAvatarFallback = useCallback(
    (userInfo?: UserInfo | null): string => {
      if (!userInfo) return "?";
      const name = userInfo.name;
      if (name) {
        const parts = name.split(" ");
        if (parts.length > 1) {
          return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
      }
      return userInfo.username?.substring(0, 2).toUpperCase() ?? "??";
    },
    [],
  );

  // --- Instantiate columns ---
  const columns = React.useMemo(
    () =>
      getColumns({
        apiBaseUrl,
        sortBy,
        sortDesc,
        updateNeededFilters: aggregatedStatusFilters,
        userStatusFilters,
        dropdownRef,
        dropdownContentRef,
        isDropdownOpen,
        setIsDropdownOpen,
        setSortBy,
        setSortDesc,
        setPage,
        handleTempFilterChange: handleTempFilterChange as (
          value: string,
          checked: boolean,
        ) => void,
        resetUpdateNeededFilters,
        getUserDisplayName,
        getAvatarFallback,
        handleUserStatusFilterChange: handleUserStatusFilterChange as (
          value: string,
          checked: boolean,
        ) => void,
        resetUserStatusFilters,
      }),
    [
      apiBaseUrl,
      sortBy,
      sortDesc,
      aggregatedStatusFilters,
      userStatusFilters,
      isDropdownOpen,
      handleTempFilterChange,
      resetUpdateNeededFilters,
      getUserDisplayName,
      getAvatarFallback,
      handleUserStatusFilterChange,
      resetUserStatusFilters,
    ],
  );

  // --- Pagination handler ---
  const handlePaginationChange = (updater: Updater<PaginationState>) => {
    const newPaginationState =
      typeof updater === "function"
        ? updater({ pageIndex: page - 1, pageSize })
        : updater;

    if (newPaginationState.pageSize !== pageSize) {
      setPageSize(newPaginationState.pageSize);
      setPage(1);
    } else if (newPaginationState.pageIndex + 1 !== page) {
      setPage(newPaginationState.pageIndex + 1);
    }
  };

  // --- Render ---
  return (
    <div className="container mx-auto py-6">
      <h1 className="mb-6 bg-gradient-to-r from-base-content/80 to-base-content bg-clip-text text-3xl font-bold text-transparent">
        Articles
      </h1>

      {error && (
        <div role="alert" className="mb-4 alert alert-error">
          {/* Error SVG and content */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 shrink-0 stroke-current"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2 2m2-2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div>
            <h3 className="font-bold">Error</h3>
            <div className="text-xs">{error}</div>
          </div>
        </div>
      )}

      {/* Filter Controls */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          type="text"
          placeholder="Search title..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="input-bordered input input-sm w-full max-w-xs focus:outline-none"
        />
        <select
          className="select-bordered select w-full max-w-[180px] select-sm focus:outline-none"
          value={persistentStatusFilter}
          disabled={userStatusFilters.length < VALID_USER_STATUSES.length}
          onChange={(e) => {
            setPersistentStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">All User Statuses</option>
          <option value="Open">Open</option>
          <option value="Needs Review">Needs Review</option>
          <option value="In Progress">In Progress</option>
          <option value="Completed">Completed</option>
          <option value="N/A">N/A</option>
        </select>
        <select
          className="select-bordered select w-full max-w-[180px] select-sm focus:outline-none"
          value={assignedToFilter}
          onChange={(e) => {
            setAssignedToFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">All Assigned</option>
          {[
            ...new Map(
              data?.articles
                .map((article) => article.assigned_to)
                .filter((user): user is UserStamp => !!user)
                .map((user) => [user.username, user]),
            ).values(),
          ]
            .sort((a, b) =>
              getUserDisplayName(a).localeCompare(getUserDisplayName(b)),
            )
            .map((user) => (
              <option key={user?.username} value={user?.username}>
                {getUserDisplayName(user)}
              </option>
            ))}
        </select>
        <select
          className="select-bordered select w-full max-w-[180px] select-sm focus:outline-none"
          value={releaseVersionFilter}
          onChange={(e) => {
            setReleaseVersionFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="all">All Release Versions</option>
          {allReleaseVersions.map((version) => (
            <option key={version} value={version}>
              {version}
            </option>
          ))}
        </select>
      </div>

      {/* Use SimpleTable component */}
      {data?.articles || loading ? (
        <>
          <SimpleTable
            columns={columns} // Use the memoized columns
            data={data?.articles ?? []}
            isLoading={loading}
            skeletonRowCount={pageSize / 2}
            pageCount={data?.total_pages ?? 0}
            pagination={{ pageIndex: page - 1, pageSize }}
            onPaginationChange={handlePaginationChange}
          />
          <div className="py-5">
            <p>
              <span className="font-medium">Total articles:</span>{" "}
              {data?.total_articles}
            </p>
          </div>
        </>
      ) : (
        !loading && (
          <div className="py-10 text-center opacity-50">
            No articles found matching your criteria.
          </div>
        )
      )}
    </div>
  );
};

export default ArticlesListPage;
